import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, Select, Checkbox } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useWarehouses, useCreateWarehouse, useUpdateWarehouse, useDeleteWarehouse } from '@/hooks/useInventory';
import type { Warehouse } from '@/services/warehouse.service';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import api from '@/services/api.service';

const warehouseTypes = ['Store', 'Transit', 'Work-In-Progress', 'Scrap', 'Quarantine', 'Other'];

function WarehouseInventoryAccountSelect(props: React.ComponentProps<typeof Select>) {
  const { data: accountsResponse } = useQuery({
    queryKey: ['chart-of-accounts-warehouse-inv'],
    queryFn: async () => {
      const res = await api.get<any>('/finance/chart-of-accounts');
      const body = res?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return list as {
        id: string;
        account_code: string;
        account_name: string;
        is_group: boolean;
        account_type?: string;
      }[];
    },
  });
  const options = useMemo(() => {
    const list = Array.isArray(accountsResponse) ? accountsResponse : [];
    return list
      .filter((a) => !a.is_group)
      .map((a) => ({ value: a.id, label: `${a.account_code} - ${a.account_name}` }));
  }, [accountsResponse]);
  return (
    <Select
      placeholder="Inventory GL (for stock transfer JVs)"
      allowClear
      showSearch
      optionFilterProp="label"
      options={options}
      {...props}
    />
  );
}

const WarehousesPage: React.FC = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null);
  const warehouseType = Form.useWatch('warehouse_type', form);

  const { data: warehouses = [], isLoading } = useWarehouses();
  const createWarehouse = useCreateWarehouse();
  const updateWarehouse = useUpdateWarehouse();
  const deleteWarehouse = useDeleteWarehouse();

  // Modal uses destroyOnClose — set values after the Form remounts, not before open.
  useEffect(() => {
    if (!isModalVisible) return;
    if (editingWarehouse) {
      form.setFieldsValue({
        warehouse_code: editingWarehouse.warehouse_code,
        warehouse_name: editingWarehouse.warehouse_name,
        warehouse_type: editingWarehouse.warehouse_type || 'Store',
        project_id: editingWarehouse.project_id || undefined,
        inventory_account_id: editingWarehouse.inventory_account_id || undefined,
        is_main_warehouse: editingWarehouse.is_main_warehouse ?? false,
        is_active: editingWarehouse.is_active !== false,
        address: editingWarehouse.address ?? undefined,
        city: editingWarehouse.city ?? undefined,
        state: editingWarehouse.state ?? undefined,
        country: editingWarehouse.country ?? undefined,
        phone: editingWarehouse.phone ?? undefined,
        email: editingWarehouse.email ?? undefined,
      });
      return;
    }
    form.resetFields();
    form.setFieldsValue({ warehouse_type: 'Store', is_main_warehouse: false, is_active: true });
  }, [isModalVisible, editingWarehouse, form]);

  const columns: ColumnsType<Warehouse> = [
    {
      title: 'Code',
      dataIndex: 'warehouse_code',
      key: 'warehouse_code',
      width: 140,
    },
    {
      title: 'Name',
      dataIndex: 'warehouse_name',
      key: 'warehouse_name',
      width: 200,
    },
    {
      title: 'Type',
      dataIndex: 'warehouse_type',
      key: 'warehouse_type',
      width: 160,
      render: (type: string, record) => (
        <Space size={4}>
          <Tag color="blue">{type || 'Store'}</Tag>
          {record.project_id ? <Tag color="purple">Project WIP</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Project',
      dataIndex: 'project_id',
      key: 'project_id',
      width: 120,
      ellipsis: true,
      render: (pid: string | null | undefined) =>
        pid ? <Tag color="purple">{pid.slice(0, 8)}…</Tag> : '—',
    },
    {
      title: 'Main',
      dataIndex: 'is_main_warehouse',
      key: 'is_main_warehouse',
      width: 80,
      align: 'center',
      render: (val: boolean) => (val ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (val: boolean) =>
        val !== false ? <Tag color="green">Active</Tag> : <Tag color="default">Inactive</Tag>,
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      width: 120,
      ellipsis: true,
      render: (city: string) => city || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 160,
      render: (_: unknown, record: Warehouse) => (
        <Space size="small">
          <Link to={`/scm/warehouse/warehouses/${record.id}`}>
            <Button type="text" icon={<InboxOutlined />} size="small" title="Bins" />
          </Link>
          <Button type="text" icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingWarehouse(null);
    setIsModalVisible(true);
  };

  const handleEdit = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: 'Delete Warehouse',
      content: 'Are you sure you want to delete this warehouse? It will be marked inactive.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => deleteWarehouse.mutate(id),
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        warehouse_code: values.warehouse_code,
        warehouse_name: values.warehouse_name,
        warehouse_type: values.warehouse_type,
        project_id:
          values.warehouse_type === 'Work-In-Progress' ? values.project_id || null : null,
        inventory_account_id: values.inventory_account_id || null,
        is_main_warehouse: values.is_main_warehouse,
        is_active: values.is_active,
        address: values.address || null,
        city: values.city || null,
        state: values.state || null,
        country: values.country || null,
        phone: values.phone || null,
        email: values.email || null,
      };
      if (editingWarehouse) {
        await updateWarehouse.mutateAsync({ id: editingWarehouse.id, data: payload });
      } else {
        await createWarehouse.mutateAsync(payload);
      }
      setIsModalVisible(false);
      form.resetFields();
      setEditingWarehouse(null);
    } catch {
      // Form validation or API error - mutations show toast
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingWarehouse(null);
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="Warehouses"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Warehouse
          </Button>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={warehouses}
          loading={isLoading}
          scroll={{ x: 1100 }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (t) => `Total ${t} warehouses`,
          }}
        />
      </Card>

      <Modal
        title={editingWarehouse ? 'Edit Warehouse' : 'Add Warehouse'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={editingWarehouse ? 'Update' : 'Create'}
        confirmLoading={createWarehouse.isPending || updateWarehouse.isPending}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="warehouse_code"
            label="Warehouse Code"
            rules={[
              { required: true, message: 'Please enter warehouse code' },
              { max: 50, message: 'Max 50 characters' },
            ]}
          >
            <Input placeholder="e.g. WH-MAIN" />
          </Form.Item>
          <Form.Item
            name="warehouse_name"
            label="Warehouse Name"
            rules={[
              { required: true, message: 'Please enter warehouse name' },
              { max: 255, message: 'Max 255 characters' },
            ]}
          >
            <Input placeholder="e.g. Main Warehouse" />
          </Form.Item>
          <Form.Item name="warehouse_type" label="Type" rules={[{ required: true }]}>
            <Select
              placeholder="Select type"
              options={warehouseTypes.map((t) => ({ label: t, value: t }))}
              onChange={(t) => {
                if (t !== 'Work-In-Progress') form.setFieldsValue({ project_id: undefined });
              }}
            />
          </Form.Item>
          {warehouseType === 'Work-In-Progress' ? (
            <Form.Item
              name="project_id"
              label="Project (WIP / site store)"
              rules={[{ required: true, message: 'WIP warehouses must be linked to a project' }]}
              extra="Strict: stock in this warehouse may only move for this project."
            >
              <ProjectSelector
                value={form.getFieldValue('project_id')}
                onChange={(projectId) => form.setFieldsValue({ project_id: projectId })}
              />
            </Form.Item>
          ) : null}
          <Form.Item
            name="inventory_account_id"
            label="Inventory GL account"
            extra="Used for stock transfer ship/receive journal vouchers. Company also needs Inventory-in-Transit (setting or COA 1210)."
          >
            <WarehouseInventoryAccountSelect />
          </Form.Item>
          <Form.Item name="is_main_warehouse" valuePropName="checked">
            <Checkbox>Default / Main warehouse</Checkbox>
          </Form.Item>
          <Form.Item name="is_active" valuePropName="checked">
            <Checkbox>Active</Checkbox>
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} placeholder="Address" />
          </Form.Item>
          <Space style={{ width: '100%' }} size="middle">
            <Form.Item name="city" label="City" style={{ marginBottom: 0 }}>
              <Input placeholder="City" />
            </Form.Item>
            <Form.Item name="state" label="State" style={{ marginBottom: 0 }}>
              <Input placeholder="State" />
            </Form.Item>
            <Form.Item name="country" label="Country" style={{ marginBottom: 0 }}>
              <Input placeholder="Country" />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} size="middle">
            <Form.Item name="phone" label="Phone" style={{ marginBottom: 0 }}>
              <Input placeholder="Phone" />
            </Form.Item>
            <Form.Item name="email" label="Email" style={{ marginBottom: 0 }}>
              <Input type="email" placeholder="Email" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default WarehousesPage;
